import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DateTimeSettings, DeviceAsset, Vendors } from '@cdw-ae/common-data';
import { CdwDividerModule } from '@cdw-ae/common-ui-components-divider';
import {
  CdwTableColumn,
  CdwTableComponent,
} from '@cdw-ae/common-ui-components-table';
import { TimezoneDateTimePipe } from '@cdw-ae/date-time-angular-service';
import { formatDuration } from '@cdw-ae/date-time-service';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Observable, Subject, takeUntil } from 'rxjs';
import { selectCustomerDateTimeSettings } from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';

interface RecentUsage {
  dateTime: string;
  activeTime: string;
}

interface RecentUser {
  usageOrder: number;
  email: string;
}

@Component({
  selector: 'device-usage',
  standalone: true,
  imports: [
    CommonModule,
    CdwTableComponent,
    CdwDividerModule,
    TimezoneDateTimePipe,
  ],
  templateUrl: './device-usage.component.html',
  styleUrl: './device-usage.component.scss',
})
export class DeviceUsageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() deviceId: string | null = null;
  @Input() deviceData: DeviceAsset | null = null;

  // Tailwind colors for styling
  public readonly gray50 = TailwindColors.gray50;
  public readonly gray300 = TailwindColors.gray300;

  public firstEnrollmentTime: Date | null = null;
  public lastEnrollmentTime: Date | null = null;
  // H3: Removed unused loading and error properties

  // Date/time settings observable
  dateTimeSettings$!: Observable<DateTimeSettings>;

  private readonly store = inject(Store);
  private destroy$ = new Subject<void>();
  private currentSettings: DateTimeSettings | null = null;

  // Most Recent Usage table configuration
  public recentUsageColumns: CdwTableColumn[] = [
    { header: 'Date', field: 'dateTime', width: '50%', align: 'left' },
    { header: 'Active Time', field: 'activeTime', width: '50%', align: 'left' },
  ];

  public recentUsageData: RecentUsage[] = [];

  // Most Recent Users table configuration
  public recentUsersColumns: CdwTableColumn[] = [
    { header: 'Usage Order', field: 'usageOrder', width: '30%', align: 'left' },
    { header: 'Email', field: 'email', width: '70%', align: 'left' },
  ];

  public recentUsersData: RecentUser[] = [];

  constructor() {
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);
  }

  ngOnInit(): void {
    // Subscribe to settings and process device data when settings are available
    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentSettings = settings;
        if (this.deviceData && this.currentSettings) {
          this.processDeviceData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.deviceData && this.currentSettings) {
      this.processDeviceData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processDeviceData(): void {
    if (!(this.deviceData && this.currentSettings)) return;

    const googleData = this.deviceData.vendorProperties?.[Vendors.GOOGLE];
    const settings = this.currentSettings;

    // Load active time ranges data, most recent first (EDUS-2631).
    // Google returns dates as ISO calendar strings (YYYY-MM-DD) which sort
    // lexically = chronologically, so a plain string compare handles month
    // and year boundaries correctly. Undated rows sink to the bottom.
    this.recentUsageData =
      googleData?.activeTimeRanges && Array.isArray(googleData.activeTimeRanges)
        ? [...googleData.activeTimeRanges]
            .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
            .map((range) => ({
              dateTime: range.date
                ? DateTime.fromISO(range.date).toFormat(
                    settings.dateFormat === 'CANADIAN'
                      ? 'yyyy-MM-dd'
                      : 'MM/dd/yy'
                  )
                : 'N/A',
              activeTime: range.activeTime
                ? formatDuration(range.activeTime)
                : 'N/A',
            }))
        : [];

    // Load recent users data
    this.recentUsersData =
      googleData?.recentUsers && Array.isArray(googleData.recentUsers)
        ? googleData.recentUsers.map((user, index) => ({
            usageOrder: index + 1,
            email: user.email || 'Unknown',
          }))
        : [];

    // Load enrollment times
    const firstDate = googleData?.firstEnrollmentTime
      ? new Date(googleData.firstEnrollmentTime)
      : null;
    this.firstEnrollmentTime =
      firstDate && !Number.isNaN(firstDate.getTime()) ? firstDate : null;

    const lastDate = googleData?.lastEnrollmentTime
      ? new Date(googleData.lastEnrollmentTime)
      : null;
    this.lastEnrollmentTime =
      lastDate && !Number.isNaN(lastDate.getTime()) ? lastDate : null;
  }

}
